import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { 
  Ticket, Plus, Search, Filter, Clock, CheckCircle, AlertCircle, MessageSquare,
  ChevronDown, Paperclip, Send, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEPARTMENT_OPTIONS = [
  { value: 'admin', label: 'Admin Department' },
  { value: 'dm_channel', label: 'DM Channel' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'legal', label: 'Legal Department' },
  { value: 'finance', label: 'Finance Department' },
  { value: 'operations', label: 'Operations & Development' },
  { value: 'architect', label: 'Architect Department' },
  { value: 'it', label: 'IT Department' },
  { value: 'hr', label: 'Human Resources' },
  { value: 'customer', label: 'Customer' },
];

const MARKETING_SECTIONS = [
  { value: 'barter', label: 'Barter' },
  { value: 'tele_calling', label: 'Tele Calling' },
  { value: 'digital_marketing', label: 'Digital Marketing' },
  { value: 'marketing_sub_dept', label: 'Marketing Sub Department' },
  { value: 'reception', label: 'Reception' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'stone' },
  { value: 'medium', label: 'Medium', color: 'blue' },
  { value: 'high', label: 'High', color: 'amber' },
  { value: 'critical', label: 'Critical', color: 'red' },
];

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'amber', icon: Clock },
  assigned: { label: 'Assigned', color: 'blue', icon: AlertCircle },
  in_progress: { label: 'In Progress', color: 'purple', icon: AlertCircle },
  resolved: { label: 'Resolved', color: 'green', icon: CheckCircle },
  closed: { label: 'Closed', color: 'stone', icon: CheckCircle },
};

const TicketsPage = () => {
  const { user } = useOutletContext();
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newTicket, setNewTicket] = useState({
    subject: '',
    description: '',
    targetDepartment: '',
    targetSection: '',
    priority: 'medium'
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    filterTickets();
  }, [tickets, searchQuery, statusFilter]);

  const fetchTickets = async () => {
    const token = localStorage.getItem('crm_token');
    try {
      const response = await axios.get(`${API}/crm/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const filterTickets = () => {
    let filtered = [...tickets];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => 
        t.subject?.toLowerCase().includes(query) ||
        t.ticketId?.toLowerCase().includes(query) ||
        t.description?.toLowerCase().includes(query)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(t => t.status === statusFilter);
    }

    setFilteredTickets(filtered);
  };

  const createTicket = async () => {
    if (!newTicket.subject || !newTicket.description || !newTicket.targetDepartment) {
      toast.error('Please fill all required fields');
      return;
    }

    const token = localStorage.getItem('crm_token');
    setSubmitting(true);

    try {
      const payload = {
        subject: newTicket.subject,
        description: newTicket.description,
        targetDepartment: newTicket.targetDepartment,
        targetSection: newTicket.targetDepartment === 'marketing' ? newTicket.targetSection : null,
        priority: newTicket.priority
      };

      await axios.post(`${API}/crm/tickets`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Ticket created successfully');
      setCreateDialogOpen(false);
      setNewTicket({ subject: '', description: '', targetDepartment: '', targetSection: '', priority: 'medium' });
      fetchTickets();
    } catch (error) {
      toast.error('Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const updateTicketStatus = async (ticketId, newStatus) => {
    const token = localStorage.getItem('crm_token');
    try {
      await axios.patch(
        `${API}/crm/tickets/${ticketId}/status?new_status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ticket status updated');
      fetchTickets();
      setSelectedTicket(null);
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };

  const openCount = tickets.filter(t => t.status === 'open').length;
  const inProgressCount = tickets.filter(t => t.status === 'in_progress' || t.status === 'assigned').length;

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-stone-900">Tickets</h1>
          <p className="text-stone-500 mt-1">Manage support tickets and requests</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="mt-4 md:mt-0 bg-emerald-900 hover:bg-emerald-800">
              <Plus className="w-4 h-4 mr-2" />
              Create Ticket
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create New Ticket</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Subject *</label>
                <Input
                  placeholder="Brief description of the issue"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Department *</label>
                <Select 
                  value={newTicket.targetDepartment} 
                  onValueChange={(v) => setNewTicket({ ...newTicket, targetDepartment: v, targetSection: '' })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPARTMENT_OPTIONS.filter(d => d.value !== user?.department).map((dept) => (
                      <SelectItem key={dept.value} value={dept.value}>{dept.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {newTicket.targetDepartment === 'marketing' && (
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Marketing Section</label>
                  <Select 
                    value={newTicket.targetSection} 
                    onValueChange={(v) => setNewTicket({ ...newTicket, targetSection: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select section (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {MARKETING_SECTIONS.map((section) => (
                        <SelectItem key={section.value} value={section.value}>{section.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Priority</label>
                <Select 
                  value={newTicket.priority} 
                  onValueChange={(v) => setNewTicket({ ...newTicket, priority: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Description *</label>
                <Textarea
                  placeholder="Detailed description of the issue..."
                  rows={4}
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={createTicket} 
                  disabled={submitting}
                  className="bg-emerald-900 hover:bg-emerald-800"
                >
                  {submitting ? 'Creating...' : 'Create Ticket'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-stone-100 rounded-full">
              <Ticket className="w-5 h-5 text-stone-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{tickets.length}</p>
              <p className="text-xs text-stone-500">Total Tickets</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-amber-100 rounded-full">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-900">{openCount}</p>
              <p className="text-xs text-amber-700">Open</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <AlertCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-900">{inProgressCount}</p>
              <p className="text-xs text-blue-700">In Progress</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-full">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-900">
                {tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length}
              </p>
              <p className="text-xs text-green-700">Resolved</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                placeholder="Search tickets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tickets List */}
      <Card>
        <CardContent className="p-0">
          {filteredTickets.length === 0 ? (
            <div className="text-center py-12">
              <Ticket className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">No tickets found</p>
              <Button 
                className="mt-4 bg-emerald-900 hover:bg-emerald-800"
                onClick={() => setCreateDialogOpen(true)}
              >
                Create your first ticket
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {filteredTickets.map((ticket) => {
                const StatusIcon = STATUS_CONFIG[ticket.status]?.icon || Clock;
                const statusColor = STATUS_CONFIG[ticket.status]?.color || 'stone';
                const priorityConfig = PRIORITY_OPTIONS.find(p => p.value === ticket.priority);
                
                return (
                  <div 
                    key={ticket.ticketId} 
                    className="p-4 hover:bg-stone-50 cursor-pointer"
                    onClick={() => setSelectedTicket(ticket)}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-xs text-stone-400">{ticket.ticketId}</span>
                          <span className={`px-2 py-0.5 text-xs rounded bg-${priorityConfig?.color}-100 text-${priorityConfig?.color}-700`}>
                            {priorityConfig?.label}
                          </span>
                        </div>
                        <h3 className="font-medium text-stone-900">{ticket.subject}</h3>
                        <p className="text-sm text-stone-500 mt-1 line-clamp-1">{ticket.description}</p>
                        <p className="text-xs text-stone-400 mt-2">
                          By {ticket.raisedByName} • {new Date(ticket.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs rounded-full bg-${statusColor}-100 text-${statusColor}-700`}>
                          <StatusIcon className="w-3 h-3" />
                          {STATUS_CONFIG[ticket.status]?.label}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ticket Detail Dialog */}
      {selectedTicket && (
        <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="font-mono text-sm text-stone-400">{selectedTicket.ticketId}</span>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <h2 className="text-xl font-bold text-stone-900">{selectedTicket.subject}</h2>
                <p className="text-sm text-stone-500 mt-1">
                  Raised by {selectedTicket.raisedByName} • {new Date(selectedTicket.createdAt).toLocaleString()}
                </p>
              </div>
              
              <div className="flex flex-wrap gap-2">
                <span className={`px-3 py-1 text-xs rounded-full bg-${STATUS_CONFIG[selectedTicket.status]?.color}-100 text-${STATUS_CONFIG[selectedTicket.status]?.color}-700`}>
                  {STATUS_CONFIG[selectedTicket.status]?.label}
                </span>
                <span className={`px-3 py-1 text-xs rounded-full bg-${PRIORITY_OPTIONS.find(p => p.value === selectedTicket.priority)?.color}-100 text-${PRIORITY_OPTIONS.find(p => p.value === selectedTicket.priority)?.color}-700`}>
                  {PRIORITY_OPTIONS.find(p => p.value === selectedTicket.priority)?.label} Priority
                </span>
              </div>

              <div className="p-4 bg-stone-50 rounded-lg">
                <p className="text-stone-700 whitespace-pre-wrap">{selectedTicket.description}</p>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm font-medium text-stone-700 mb-2">Target Department</p>
                <p className="text-stone-600">
                  {DEPARTMENT_OPTIONS.find(d => d.value === selectedTicket.targetDepartment)?.label}
                  {selectedTicket.targetSection && ` - ${MARKETING_SECTIONS.find(s => s.value === selectedTicket.targetSection)?.label}`}
                </p>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm font-medium text-stone-700 mb-2">Deadline</p>
                <p className="text-stone-600">
                  {new Date(selectedTicket.deadline).toLocaleString()} (7 PM same day)
                </p>
              </div>

              <div className="border-t pt-4 flex gap-2">
                {selectedTicket.status === 'open' && (
                  <Button
                    onClick={() => updateTicketStatus(selectedTicket.ticketId, 'in_progress')}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Start Working
                  </Button>
                )}
                {(selectedTicket.status === 'open' || selectedTicket.status === 'in_progress') && (
                  <Button
                    onClick={() => updateTicketStatus(selectedTicket.ticketId, 'resolved')}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Mark Resolved
                  </Button>
                )}
                {selectedTicket.status === 'resolved' && (
                  <Button
                    onClick={() => updateTicketStatus(selectedTicket.ticketId, 'closed')}
                    variant="outline"
                  >
                    Close Ticket
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default TicketsPage;
